import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  List,
  ListItem,
  ListIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Divider,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import Header from "../Common/Header";
import Footer from "../Common/Footer";
import { MdCheck } from "react-icons/md";

interface AccessibilitySection {
  id: string;
  title: string;
  content: React.ReactNode;
  subsections?: AccessibilitySection[];
}

const AccessibilityStatement: React.FC = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("blue.50", "blue.900");
  const importantBg = useColorModeValue("red.50", "red.900");
  const footerBg = useColorModeValue("gray.50", "gray.700");

  const accessibilityData: AccessibilitySection[] = [
    {
      id: "1",
      title: "Commitment to Accessibility",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            VFrequentists LLP ("Company," "We,") is committed to ensuring that
            QIQ Health ("App," "Platform") is accessible to all qualified
            healthcare professionals, including those with disabilities.
          </Text>
          <Text>
            Our goal is to provide a robust, inclusive, and user-friendly
            experience in compliance with applicable accessibility standards
            while preserving the professional context and clinical functionality
            of the App.
          </Text>
        </VStack>
      ),
    },
    {
      id: "2",
      title: "Accessibility Standards and Guidelines",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text mb={3}>
            QIQ Health is designed and developed to align with the following
            accessibility standards and guidelines:
          </Text>
          <List spacing={2}>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Web Content Accessibility Guidelines (WCAG) 2.2 – Level AA:
              </Text>{" "}
              Ensures perceivable, operable, understandable, and robust user
              experience.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Section 508 of the U.S. Rehabilitation Act:
              </Text>{" "}
              Provides accessibility for federal employees and members of the
              public with disabilities.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                EN 301 549 (EU Accessibility Standard):
              </Text>{" "}
              Meets accessibility requirements for ICT products and services in
              Europe.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Apple Human Interface Guidelines for Accessibility:
              </Text>{" "}
              Utilizes iOS-specific accessibility features.
            </ListItem>
          </List>
        </VStack>
      ),
    },
    {
      id: "3",
      title: "Accessibility Features",
      content: (
        <VStack align="stretch" spacing={4}>
          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Perceivable Features
            </Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  VoiceOver Compatibility:
                </Text>{" "}
                All interactive elements labeled with descriptive text.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Dynamic Type Support:
                </Text>{" "}
                Text scales appropriately with user font settings.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Customizable Contrast:
                </Text>{" "}
                High-contrast color scheme compliant with WCAG AA.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Alt Text for Images:
                </Text>{" "}
                Professional context descriptions for icons and non-text
                elements.
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Operable Features
            </Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Full Keyboard Navigation:
                </Text>{" "}
                Supports external keyboards and switch controls.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Accessible Gestures:
                </Text>{" "}
                Alternative gesture operations for single‐hand use.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Focus Indicators:
                </Text>{" "}
                Clear visual focus outlines for interactive elements.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Touch Target Size:
                </Text>{" "}
                Minimum 44×44 pt touch areas.
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Understandable Features
            </Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Consistent Navigation:
                </Text>{" "}
                Standardized navigation patterns for professional workflows.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Error Identification:
                </Text>{" "}
                Descriptive error messages with remediation guidance.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Plain Language:
                </Text>{" "}
                Professional terminology with contextual tooltips.
              </ListItem>
            </List>
          </Box>

          <Box>
            <Heading size="sm" color="blue.600" mb={3}>
              Robust Features
            </Heading>
            <List spacing={2}>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  Assistive Technology Compatibility:
                </Text>{" "}
                Tested with VoiceOver, Switch Control, and external braille
                displays.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  ARIA Implementation:
                </Text>{" "}
                Accessible Rich Internet Application attributes on web
                components.
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                  •
                </ListIcon>
                <Text as="span" fontWeight="semibold">
                  API Accessibility:
                </Text>{" "}
                Adoption of Apple's UIAccessibility APIs for native components.
              </ListItem>
            </List>
          </Box>
        </VStack>
      ),
    },
    {
      id: "4",
      title: "Accessibility Testing Process",
      content: (
        <VStack align="stretch" spacing={4}>
          <List spacing={2}>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Automated Testing:
              </Text>{" "}
              Continuous integration scans with Accessibility Inspector, axe for
              iOS, and XCUITest.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Manual Expert Review:
              </Text>{" "}
              Certified accessibility specialists validate compliance with WCAG
              and Apple guidelines.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                User Testing with Assistive Technologies:
              </Text>{" "}
              Real-world testing by healthcare professionals using screen
              readers, switch devices, and voice control.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Regression Testing:
              </Text>{" "}
              Accessibility tests incorporated into every release cycle.
            </ListItem>
          </List>
        </VStack>
      ),
    },
    {
      id: "5",
      title: "Conformance Status",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            Based on internal audits as of{" "}
            <Text as="span" fontWeight="semibold">
              August 11, 2025
            </Text>
            , QIQ Health:
          </Text>
          <List spacing={2}>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Partially Conforms
              </Text>{" "}
              with WCAG 2.2 Level AA.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Meets
              </Text>{" "}
              applicable Section 508 and EN 301 549 requirements for native
              mobile applications.
            </ListItem>
          </List>
          <Box
            bg={importantBg}
            border="2px solid"
            borderColor="red.300"
            borderRadius="md"
            p={4}
          >
            <Text fontWeight="bold" color="red.700">
              Areas identified for improvement include advanced dashboard charts
              and complex tabular displays. Resolution is targeted for Q4 2025.
            </Text>
          </Box>
        </VStack>
      ),
    },
    {
      id: "6",
      title: "Known Accessibility Limitations",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text mb={3}>Current limitations and planned resolutions:</Text>
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Area</Th>
                  <Th>Limitation</Th>
                  <Th>Planned Resolution</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td fontWeight="semibold">Interactive Charts</Td>
                  <Td>
                    VoiceOver announces chart as an image without data context
                  </Td>
                  <Td>
                    Provide data table option and chart data descriptions by Q4
                    2025
                  </Td>
                </Tr>
                <Tr>
                  <Td fontWeight="semibold">Complex Tables</Td>
                  <Td>
                    Horizontal scrolling required may impair keyboard navigation
                  </Td>
                  <Td>
                    Implement sticky headers and ARIA grid roles by Q3 2025
                  </Td>
                </Tr>
                <Tr>
                  <Td fontWeight="semibold">Gesture-Only Actions</Td>
                  <Td>
                    Some advanced clinical workflows rely on multi-finger
                    gestures
                  </Td>
                  <Td>
                    Add alternative on-screen buttons and keyboard shortcuts by
                    Q3 2025
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        </VStack>
      ),
    },
    {
      id: "7",
      title: "Feedback and Contact Information",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text mb={3}>
            We welcome accessibility feedback from healthcare professionals:
          </Text>
          <List spacing={2}>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Email:
              </Text>{" "}
              accessibility@weqiq.com
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Mail:
              </Text>{" "}
              VFrequentists LLP, Attn: Accessibility Team
            </ListItem>
          </List>
          <Box
            bg={importantBg}
            border="2px solid"
            borderColor="red.300"
            borderRadius="md"
            p={4}
          >
            <Text fontWeight="bold" color="red.700">
              Response Time: We aim to acknowledge feedback within 2 business
              days and provide a substantive response within 10 business days.
            </Text>
          </Box>
        </VStack>
      ),
    },
    {
      id: "8",
      title: "Continuous Improvement and Review Cycle",
      content: (
        <VStack align="stretch" spacing={4}>
          <List spacing={2}>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Quarterly Accessibility Reviews:
              </Text>{" "}
              Assess new features and address outstanding issues.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Annual Third-Party Audits:
              </Text>{" "}
              Independent accessibility audits of the full product.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Professional User Advisory Group:
              </Text>{" "}
              Solicits feedback from healthcare professionals with disabilities.
            </ListItem>
            <ListItem>
              <ListIcon as={MdCheck} color="blue.500" fontSize="sm">
                •
              </ListIcon>
              <Text as="span" fontWeight="semibold">
                Accessibility Roadmap Updates:
              </Text>{" "}
              Published semi-annually on our website.
            </ListItem>
          </List>
        </VStack>
      ),
    },
    {
      id: "9",
      title: "Legal Disclaimer and Professional Responsibility",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            QIQ Health is provided as a clinical decision-support tool for
            qualified healthcare professionals. While VFrequentists LLP strives
            to meet accessibility requirements, healthcare professionals remain
            responsible for verifying that QIQ Health is suitable for their
            individual accessibility needs and clinical workflows.
          </Text>
          <Box
            bg={importantBg}
            border="2px solid"
            borderColor="red.300"
            borderRadius="md"
            p={4}
          >
            <Text fontWeight="bold" color="red.700">
              VFrequentists LLP assumes no liability for professional decisions
              or outcomes resulting from accessibility limitations.
            </Text>
          </Box>
        </VStack>
      ),
    },
    {
      id: "10",
      title: "Acknowledgment",
      content: (
        <VStack align="stretch" spacing={4}>
          <Text>
            By using QIQ Health, healthcare professionals acknowledge that they
            have reviewed this Accessibility Statement and agree to provide
            feedback on any accessibility barriers encountered.
          </Text>
        </VStack>
      ),
    },
  ];

  return (
    <Box bg={bgColor} minH="100vh" py={8}>
      <Header minimal={false} />
      <Container maxW="container.xl" mt={10}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box
            textAlign="center"
            pb={8}
            borderBottom="3px solid"
            borderColor="blue.500"
          >
            <Heading as="h1" size="2xl" color="blue.600" mb={2}>
              Accessibility Conformance & Compliance Statement
            </Heading>
            <Heading as="h2" size="lg" color="gray.600" mb={6}>
              QIQ Health by VFrequentists LLP
            </Heading>

            <Box
              bg={headerBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              p={6}
            >
              <VStack spacing={2} align="stretch">
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Effective Date:
                  </Text>{" "}
                  January 1, 2025
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Last Updated:
                  </Text>{" "}
                  August 11, 2025
                </Text>
              </VStack>
            </Box>
          </Box>

          {/* Accessibility Content */}
          <Accordion allowMultiple defaultIndex={[0]}>
            {accessibilityData.map((section) => (
              <AccordionItem
                key={section.id}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="md"
                mb={4}
              >
                <AccordionButton
                  py={6}
                  px={6}
                  bg={headerBg}
                  _hover={{ bg: "blue.100" }}
                  _expanded={{
                    bg: "blue.200",
                    borderBottom: "2px solid",
                    borderColor: "blue.400",
                  }}
                >
                  <HStack flex="1" textAlign="left" spacing={4}>
                    <Badge colorScheme="blue" fontSize="lg" px={3} py={1}>
                      {section.id}
                    </Badge>
                    <Text fontWeight="semibold" fontSize="lg">
                      {section.title}
                    </Text>
                  </HStack>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel py={8} px={8}>
                  {section.content}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Footer */}
          <Box
            bg={footerBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            p={8}
          >
            <VStack spacing={6} align="stretch">
              <Text fontStyle="italic" color="gray.600">
                This document has been prepared in accordance with WCAG 2.2
                guidelines, Section 508 requirements, and international
                accessibility standards. This Accessibility Statement
                constitutes a legally binding commitment to accessibility
                standards between you and VFrequentists LLP.
              </Text>

              <Divider />

              <VStack spacing={2} align="stretch">
                <Text fontWeight="semibold">VFrequentists LLP</Text>
                <Text fontWeight="semibold">Team QIQ Health</Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Document Version:
                  </Text>{" "}
                  1.0
                </Text>
                <Text>
                  <Text as="span" fontWeight="semibold">
                    Status:
                  </Text>{" "}
                  Final Version - Effective as of January 1, 2025
                </Text>
              </VStack>
            </VStack>
          </Box>
        </VStack>
      </Container>
      <Footer />
    </Box>
  );
};

export default AccessibilityStatement;
